// PENTEST HARDENING (MFA required): pure branching logic for the
// signIn/confirmSignIn `nextStep.signInStep` state machine used by
// password_entry and sign_in. Deliberately free of any `aws-amplify`
// import so it can be exercised by a plain `node --test` run without the
// frontend's node_modules — see mfa_steps.test.ts.

/** Shape of Amplify's `AuthTOTPSetupDetails`, restated locally to avoid an
 * `aws-amplify` import (kept structurally compatible). */
export interface TotpSetupDetails {
  sharedSecret: string;
  getSetupUri: (appName: string, accountName?: string) => URL;
}

/** The subset of Amplify's `nextStep` shape this flow branches on. */
export interface MfaNextStep {
  signInStep: string;
  totpSetupDetails?: TotpSetupDetails;
  allowedMFATypes?: string[];
}

/** Views the sign-in component can render. */
export type SignInView =
  | { kind: "password" }
  | { kind: "new_password" }
  | { kind: "totp_setup"; totpSetupDetails: TotpSetupDetails }
  | { kind: "totp_code" }
  | { kind: "mfa_selection"; allowedMFATypes: string[] }
  | { kind: "signed_in" }
  | { kind: "unsupported_step"; signInStep: string };

/**
 * Maps a `signIn`/`confirmSignIn` result to the view the sign-in component
 * should render next.
 */
export function nextViewForStep(
  nextStep: MfaNextStep | undefined,
  isSignedIn: boolean | undefined
): SignInView {
  if (isSignedIn) {
    return { kind: "signed_in" };
  }
  if (!nextStep) {
    return { kind: "unsupported_step", signInStep: "UNKNOWN" };
  }

  switch (nextStep.signInStep) {
    case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD":
    case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
    case "NEW_PASSWORD_REQUIRED":
      return { kind: "new_password" };

    case "CONTINUE_SIGN_IN_WITH_TOTP_SETUP":
      if (!nextStep.totpSetupDetails) {
        return { kind: "unsupported_step", signInStep: nextStep.signInStep };
      }
      return {
        kind: "totp_setup",
        totpSetupDetails: nextStep.totpSetupDetails,
      };

    case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
      return { kind: "totp_code" };

    case "CONTINUE_SIGN_IN_WITH_MFA_SELECTION":
      return {
        kind: "mfa_selection",
        allowedMFATypes: nextStep.allowedMFATypes ?? [],
      };

    default:
      return { kind: "unsupported_step", signInStep: nextStep.signInStep };
  }
}

/** Plain-language mapping for the Cognito exceptions the MFA steps can throw.
 * Returns undefined for anything this helper doesn't have a specific
 * message for, so callers can fall back to their existing handling. */
export function mfaErrorMessage(errorName: string | undefined): string | undefined {
  switch (errorName) {
    case "CodeMismatchException":
      return "That code didn't match. Please try again.";
    case "EnableSoftwareTokenMFAException":
      return "Couldn't enable the authenticator app. Please try again.";
    case "ExpiredCodeException":
      return "That code expired. Please enter a new one.";
    default:
      return undefined;
  }
}
